import type { Currency } from './currency.js';
import {
  type DonationStatus,
  isMatchableDonationStatus,
  nextPaymentStatuses,
  type PaymentStatus,
} from './donation-status.js';
import { InvalidStatusTransitionError, NegativeAmountError } from './errors.js';
import {
  type ConversionRate,
  compare,
  convertBetween,
  type Money,
  money,
  subtract,
  zero,
} from './money.js';

/**
 * What a donation is still owed, given the donation-side amounts of its
 * confirmed and pending matches. Pending rows count: a cross-currency match
 * waiting for a second person has already claimed that part of the donation,
 * and offering it again would let two operators settle the same money twice.
 *
 * Over-collection is not clamped to zero — it is a defect the caller must see,
 * because `account_transactions_allocation_within_amount` and
 * `validateAllocation` exist to make it unreachable.
 */
export function outstanding(donationAmount: Money, allocated: Money): Money {
  const remaining = subtract(donationAmount, allocated);
  if (remaining.amountMinor < 0n) throw new NegativeAmountError(remaining.amountMinor);
  return remaining;
}

export type SettlementStatus = Extract<PaymentStatus, 'unpaid' | 'partially_settled' | 'settled'>;

/**
 * The financial axis read off the money, never off the act of matching:
 * docs/decisions/0044 §2. `refunded` and `reversed` are not answers here —
 * they are outcomes a person chose, and the writer sets them explicitly.
 */
export function settlementStatusFor(amount: Money, collected: Money): SettlementStatus {
  const order = compare(amount, collected);
  if (order < 0) throw new RangeError('A donation cannot have collected more than its amount');
  if (collected.amountMinor < 0n) throw new NegativeAmountError(collected.amountMinor);
  if (collected.amountMinor === 0n) return 'unpaid';
  return order === 0 ? 'settled' : 'partially_settled';
}

export type PaymentEdge = { readonly from: PaymentStatus; readonly to: PaymentStatus };

/**
 * The edges the writer must assert one by one to move a donation's financial
 * axis, shortest first. The axis is asymmetric — `unpaid → settled` is one edge
 * and `settled → unpaid` is two — so a writer that assigned the end state
 * directly would skip an assertion the machine exists to make
 * (docs/decisions/0023's amendment).
 */
export function paymentPath(from: PaymentStatus, to: PaymentStatus): readonly PaymentEdge[] {
  if (from === to) return [];
  const previous = new Map<PaymentStatus, PaymentStatus>();
  const seen = new Set<PaymentStatus>([from]);
  const queue: PaymentStatus[] = [from];

  while (queue.length > 0) {
    const current = queue.shift() as PaymentStatus;
    for (const next of nextPaymentStatuses(current)) {
      if (seen.has(next)) continue;
      seen.add(next);
      previous.set(next, current);
      if (next === to) {
        const edges: PaymentEdge[] = [];
        let step: PaymentStatus = to;
        while (step !== from) {
          const before = previous.get(step) as PaymentStatus;
          edges.unshift({ from: before, to: step });
          step = before;
        }
        return edges;
      }
      queue.push(next);
    }
  }

  throw new InvalidStatusTransitionError('donation.paymentStatus', from, to);
}

/**
 * The payment statuses a donation can still receive money from. `settled` owes
 * nothing, `refunded` is terminal and `not_charged` never owed anything;
 * `reversed → unpaid` is the re-match path, so a reversed donation is a
 * candidate again.
 */
export const RECEIVING_PAYMENT_STATUSES: readonly PaymentStatus[] = [
  'unpaid',
  'partially_settled',
  'reversed',
];

/**
 * Why a donation's own state closes it to money, whatever line is offered:
 * the record has been withdrawn (`cancelled`), or its payment axis expects
 * nothing more (`settled`, `refunded`), or never expected anything
 * (`not_charged`). They are exactly
 * the states the writer refuses `RECONCILIATION_DONATION_NOT_MATCHABLE`, named
 * record first, as the information hits name them.
 */
export const MATCH_CLOSED_REASONS = ['cancelled', 'settled', 'refunded', 'not_charged'] as const;
export type MatchClosedReason = (typeof MATCH_CLOSED_REASONS)[number];

export type MatchableDonationState = {
  readonly status: DonationStatus;
  readonly paymentStatus: PaymentStatus;
};

export function matchClosedReason(donation: MatchableDonationState): MatchClosedReason | null {
  if (!isMatchableDonationStatus(donation.status)) {
    return 'cancelled';
  }
  if (!RECEIVING_PAYMENT_STATUSES.includes(donation.paymentStatus)) {
    if (donation.paymentStatus === 'refunded' || donation.paymentStatus === 'not_charged') {
      return donation.paymentStatus;
    }
    return 'settled';
  }
  return null;
}

/**
 * What a match could still credit a donation, in its own currency: nothing
 * while its state closes it to money, otherwise its amount less the
 * donation-side amounts of its confirmed and pending matches, floored at zero.
 *
 * A cancelled donation owes nothing whatever its matches once held: a refund or
 * a reverse turned those matches `reversed`, so the arithmetic alone would show
 * its whole amount as still to match — docs/decisions/0044 §6. Every read that
 * shows an outstanding — the allocation panel, candidates, the basket, the
 * conversion preview and the shortfall report — reads it here.
 */
export function matchableOutstandingMinor(
  donation: MatchableDonationState & { readonly amountMinor: bigint; readonly heldMinor: bigint },
): bigint {
  if (matchClosedReason(donation) !== null) return 0n;
  const owed = donation.amountMinor - donation.heldMinor;
  return owed > 0n ? owed : 0n;
}

/**
 * What an excluded line is instead of donation money. A line leaves the
 * exceptions queue only as one of these, with a reason, and writes no ledger
 * entry — docs/plans/05-finance-reconciliation.md §5.5.
 */
export const EXCLUSION_CLASSIFICATIONS = ['fee', 'internal_transfer', 'other'] as const;
export type ExclusionClassification = (typeof EXCLUSION_CLASSIFICATIONS)[number];

export const RECONCILIATION_REFUSALS = [
  'RECONCILIATION_OVER_ALLOCATED',
  'RECONCILIATION_EXCEEDS_OUTSTANDING',
  'RECONCILIATION_ORGANIZATION_NOT_LINKED',
  'RECONCILIATION_ORGANIZATION_MISMATCH',
  'RECONCILIATION_RATE_UNAVAILABLE',
] as const;
export type ReconciliationRefusal = (typeof RECONCILIATION_REFUSALS)[number];

/** The bank line or provider capture an allocation consumes part of. */
export type AllocationLine = {
  readonly organizationId: string;
  readonly amount: Money;
  readonly allocatedMinor: bigint;
  readonly rate: ConversionRate | null;
};

/**
 * A candidate donation as the arithmetic needs it: what it is for, what its
 * confirmed and pending matches already credit it, and the rate row for its
 * currency on the line's rate day. `organizationId` is null until the money of
 * some organization reaches the donation (docs/decisions/0112 §2); such a
 * donation may take the line's money only when its institution is linked to the
 * line's organization, which is `linkedToLineOrganization`. A donation that has
 * an organization is judged by it alone.
 */
export type AllocationDonation = {
  readonly id: string;
  readonly organizationId: string | null;
  readonly linkedToLineOrganization: boolean;
  readonly amount: Money;
  readonly allocated: Money;
  readonly rate: ConversionRate | null;
};

export type AllocationRequest = {
  readonly donationId: string;
  readonly transactionAmount: Money;
};

/**
 * The donation-side amount a line-side amount credits, converted through the
 * two rate rows of one day when the currencies differ. Returns null when a
 * cross-currency allocation has no rate to convert with, which is
 * `RECONCILIATION_RATE_UNAVAILABLE`.
 */
export function donationSideAmount(
  transactionAmount: Money,
  line: Pick<AllocationLine, 'rate'>,
  donation: Pick<AllocationDonation, 'amount' | 'rate'>,
): Money | null {
  const target = donation.amount.currency;
  if (transactionAmount.currency === target) return transactionAmount;
  if (!hasRate(transactionAmount.currency, line.rate)) return null;
  if (!hasRate(target, donation.rate)) return null;
  return convertBetween(transactionAmount, line.rate, donation.rate, target);
}

function hasRate(currency: Currency, rate: ConversionRate | null): boolean {
  return rate !== null || currency === 'TRY';
}

/**
 * Every rule an allocation must pass before a `reconciliations` row exists, as
 * one pure function the writer and the matcher both use — docs/decisions/0044
 * §2 and §7. Returns the first refusal, or null when the allocation is legal.
 *
 * The amounts are judged against what the line and the donations hold *now*;
 * the writer re-reads both under a lock and calls this again, because a
 * proposal is a read and nothing about it is reserved.
 */
export function validateAllocation(
  line: AllocationLine,
  allocations: readonly AllocationRequest[],
  donations: readonly AllocationDonation[],
  options: AllocationOptions = {},
): ReconciliationRefusal | null {
  const byId = new Map(donations.map((donation) => [donation.id, donation]));
  let lineSide = zero(line.amount.currency);

  for (const allocation of allocations) {
    if (allocation.transactionAmount.amountMinor <= 0n) {
      throw new RangeError('An allocation consumes a positive amount of the line');
    }
    if (allocation.transactionAmount.currency !== line.amount.currency) {
      throw new RangeError('An allocation is denominated in the line’s currency');
    }
    lineSide = {
      amountMinor: lineSide.amountMinor + allocation.transactionAmount.amountMinor,
      currency: lineSide.currency,
    };
  }

  const remaining = line.amount.amountMinor - line.allocatedMinor;
  if (lineSide.amountMinor > remaining) return 'RECONCILIATION_OVER_ALLOCATED';

  const donationSide = new Map<string, bigint>();
  for (const allocation of allocations) {
    const donation = byId.get(allocation.donationId);
    if (!donation) throw new RangeError(`No donation was supplied for ${allocation.donationId}`);
    if (options.crossOrganization !== true) {
      if (donation.organizationId === null) {
        if (!donation.linkedToLineOrganization) return 'RECONCILIATION_ORGANIZATION_NOT_LINKED';
      } else if (donation.organizationId !== line.organizationId) {
        return 'RECONCILIATION_ORGANIZATION_MISMATCH';
      }
    }
    const credit = allocationCredit(
      allocation.transactionAmount,
      line,
      donation,
      options.fxToleranceBps ?? 0,
    );
    if (credit === null) return 'RECONCILIATION_RATE_UNAVAILABLE';
    if (credit === 'exceeds') return 'RECONCILIATION_EXCEEDS_OUTSTANDING';
    donationSide.set(donation.id, (donationSide.get(donation.id) ?? 0n) + credit.creditedMinor);
  }

  for (const [donationId, creditedMinor] of donationSide) {
    const donation = byId.get(donationId) as AllocationDonation;
    const remainingOwed = outstanding(donation.amount, donation.allocated);
    if (creditedMinor > remainingOwed.amountMinor) return 'RECONCILIATION_EXCEEDS_OUTSTANDING';
  }

  return null;
}

export type AllocationOptions = {
  /**
   * Issue #19 phase 3 (docs/decisions/0128): a line of any organization may
   * take a donation of any other. Only the person-driven `match` passes it;
   * a provider capture and a payment typed by hand still judge the donation's
   * organization against the account that received the money.
   */
  readonly crossOrganization?: boolean;
  readonly fxToleranceBps?: number;
};

/** Per-mille-of-a-percent steps: 100 is 1%. */
export const FX_TOLERANCE_DEFAULT_BPS = 100;
export const FX_TOLERANCE_MAX_BPS = 1000;

/**
 * What a currency conversion may leave over or short and still count as the
 * same amount: `toleranceBps` of what the donation owes, **rounded down**, so
 * the tolerance that applies never exceeds the one configured — 1% of 101 minor
 * units allows 1, never 2, and a donation that owes nothing allows nothing, so
 * a surplus against it is always refused. A tolerance of zero allows nothing.
 */
export function fxAllowanceMinor(outstandingMinor: bigint, toleranceBps: number): bigint {
  if (!Number.isInteger(toleranceBps) || toleranceBps < 0 || toleranceBps > FX_TOLERANCE_MAX_BPS) {
    throw new RangeError(`An FX tolerance is 0 to ${FX_TOLERANCE_MAX_BPS} basis points`);
  }
  if (toleranceBps === 0 || outstandingMinor <= 0n) return 0n;
  return (outstandingMinor * BigInt(toleranceBps)) / 10000n;
}

export type AllocationCredit = {
  /** What the donation is credited: the converted amount, or its whole outstanding when the difference is inside the tolerance. */
  readonly creditedMinor: bigint;
  /**
   * `creditedMinor` less what the line-side amount converts to, in the
   * donation's currency: positive when the donation was closed for more than
   * the money is worth, negative when a surplus was absorbed, zero otherwise.
   */
  readonly fxDifferenceMinor: bigint;
};

/**
 * The donation-side credit of one allocation, and the FX difference it absorbs
 * — docs/decisions/0128. A same-currency allocation is exact and never has a
 * difference (0044 §5: no tolerance on a short transfer). A cross-currency one
 * converts at the line's rate day; when the converted amount is within the
 * allowance of what the donation still owes — over **or** under — the donation
 * is credited exactly its outstanding and the difference is recorded, never
 * dropped. Beyond the allowance a surplus is `'exceeds'` (the person sends the
 * amount that converts to the outstanding, and the rest of the line stays for
 * the remainder) and a shortfall is credited as converted, leaving the donation
 * partly paid. Null when the conversion has no rate.
 */
export function allocationCredit(
  transactionAmount: Money,
  line: Pick<AllocationLine, 'rate'>,
  donation: Pick<AllocationDonation, 'amount' | 'allocated' | 'rate'>,
  toleranceBps: number,
): AllocationCredit | 'exceeds' | null {
  const converted = donationSideAmount(transactionAmount, line, donation);
  if (converted === null) return null;
  const owed = outstanding(donation.amount, donation.allocated).amountMinor;
  if (transactionAmount.currency === donation.amount.currency) {
    return converted.amountMinor > owed
      ? 'exceeds'
      : { creditedMinor: converted.amountMinor, fxDifferenceMinor: 0n };
  }
  const allowance = fxAllowanceMinor(owed, toleranceBps);
  if (converted.amountMinor > owed) {
    const surplus = converted.amountMinor - owed;
    return surplus <= allowance ? { creditedMinor: owed, fxDifferenceMinor: -surplus } : 'exceeds';
  }
  const shortfall = owed - converted.amountMinor;
  if (shortfall > 0n && shortfall <= allowance) {
    return { creditedMinor: owed, fxDifferenceMinor: shortfall };
  }
  return { creditedMinor: converted.amountMinor, fxDifferenceMinor: 0n };
}

function smallestSatisfying(
  satisfies: (minor: bigint) => boolean,
  ceilingMinor: bigint,
): bigint | null {
  if (ceilingMinor <= 0n || !satisfies(ceilingMinor)) return null;
  let low = 1n;
  let high = ceilingMinor;
  while (low < high) {
    const mid = (low + high) / 2n;
    if (satisfies(mid)) high = mid;
    else low = mid + 1n;
  }
  return low;
}

/**
 * The line-side amount that converts to `targetMinor` of the donation's
 * currency, or the nearest one that does not fall short, found by bisection on
 * the one converter so it can never disagree with the write. Null when no
 * amount up to `ceilingMinor` reaches the target. The smallest such amount is
 * returned, so a surplus is only ever rounding.
 */
export function lineAmountFor(
  targetMinor: bigint,
  line: Pick<AllocationLine, 'rate'>,
  lineCurrency: Currency,
  donation: Pick<AllocationDonation, 'amount' | 'rate'>,
  ceilingMinor: bigint,
): bigint | null {
  if (targetMinor <= 0n) return null;
  return smallestSatisfying((minor) => {
    const reached = donationSideAmount(money(minor, lineCurrency), line, donation);
    return reached !== null && reached.amountMinor >= targetMinor;
  }, ceilingMinor);
}

/**
 * The line-side amount a basket takes of a movement for `targetMinor` of one
 * donation, with no ceiling to guess (docs/decisions/0131 §1). It is the fair
 * conversion — `lineAmountFor`, the amount a single match's dialog suggests,
 * over a ceiling found by doubling — and the FX tolerance only settles what
 * that amount leaves over or short, as `allocationCredit` does for a single
 * match. When the converter's step is so coarse that the fair amount lands past
 * the allowance (`'exceeds'`: a euro cent is dozens of kuruş), the amount one
 * step below is taken instead if the tolerance credits it the whole target. If
 * neither does, the fair amount is returned, so the caller's `allocationCredit`
 * sees the refusal at it. A same-currency target is itself. Null when the
 * conversion has no rate.
 */
export function lineAmountNeeded(
  targetMinor: bigint,
  line: Pick<AllocationLine, 'rate'>,
  lineCurrency: Currency,
  donation: Pick<AllocationDonation, 'amount' | 'allocated' | 'rate'>,
  toleranceBps: number,
): bigint | null {
  if (targetMinor <= 0n) throw new RangeError('A basket takes a positive amount of a donation');
  if (lineCurrency === donation.amount.currency) return targetMinor;
  if (donationSideAmount(money(1n, lineCurrency), line, donation) === null) return null;
  const fair = fairLineAmount(targetMinor, line, lineCurrency, donation);
  if (fair === null) return null;
  const atFair = allocationCredit(money(fair, lineCurrency), line, donation, toleranceBps);
  if (atFair !== 'exceeds') return fair;
  const below = fair - 1n;
  if (below < 1n) return fair;
  const atBelow = allocationCredit(money(below, lineCurrency), line, donation, toleranceBps);
  return atBelow !== null && atBelow !== 'exceeds' && atBelow.creditedMinor >= targetMinor
    ? below
    : fair;
}

function fairLineAmount(
  targetMinor: bigint,
  line: Pick<AllocationLine, 'rate'>,
  lineCurrency: Currency,
  donation: Pick<AllocationDonation, 'amount' | 'rate'>,
): bigint | null {
  let ceiling = 1n;
  for (let step = 0; step < 100; step += 1) {
    const fair = lineAmountFor(targetMinor, line, lineCurrency, donation, ceiling);
    if (fair !== null) return fair;
    ceiling *= 2n;
  }
  return null;
}

export type BasketFitDonation = Pick<AllocationDonation, 'id' | 'amount' | 'allocated' | 'rate'> & {
  /** What the basket takes from the donation, in the donation's own currency. */
  readonly basketAmountMinor: bigint;
};

export type BasketFitItem = {
  readonly donationId: string;
  /** The movement-side amount the basket takes for the donation's basket amount. */
  readonly transactionAmountMinor: bigint;
  readonly creditedMinor: bigint;
  readonly fxDifferenceMinor: bigint;
};

export type BasketMovementFit =
  | {
      readonly refusal: null;
      readonly items: readonly BasketFitItem[];
      readonly requiredMinor: bigint;
    }
  | {
      readonly refusal: 'RECONCILIATION_RATE_UNAVAILABLE' | 'RECONCILIATION_EXCEEDS_OUTSTANDING';
      readonly donationId: string;
    };

/**
 * What a movement must hold to close a basket whose donations are in another
 * currency — docs/decisions/0131. A basket amount above what the donation still
 * owes is refused outright. Each other one is turned into its fair
 * movement-side amount (`lineAmountNeeded`: the smallest that converts to it,
 * the tolerance only stepping in where the converter's step is too coarse) —
 * through the one converter and the movement's frozen rates, with the FX
 * tolerance and `fxDifferenceMinor` exactly as a single cross-currency match
 * has them. The read that tells a person whether a line
 * covers the basket and the write that settles it both call this, so they
 * cannot disagree; the write derives the movement-side amounts itself and
 * never takes them from the client, so a donation cannot be under-paid by
 * sending a smaller one.
 */
export function fitBasket(
  line: Pick<AllocationLine, 'rate'>,
  lineCurrency: Currency,
  donations: readonly BasketFitDonation[],
  toleranceBps: number,
): BasketMovementFit {
  const items: BasketFitItem[] = [];
  let requiredMinor = 0n;
  for (const donation of donations) {
    if (donation.basketAmountMinor > outstanding(donation.amount, donation.allocated).amountMinor) {
      return { refusal: 'RECONCILIATION_EXCEEDS_OUTSTANDING', donationId: donation.id };
    }
    const transactionAmountMinor = lineAmountNeeded(
      donation.basketAmountMinor,
      line,
      lineCurrency,
      donation,
      toleranceBps,
    );
    if (transactionAmountMinor === null) {
      return { refusal: 'RECONCILIATION_RATE_UNAVAILABLE', donationId: donation.id };
    }
    const credit = allocationCredit(
      money(transactionAmountMinor, lineCurrency),
      line,
      donation,
      toleranceBps,
    );
    if (credit === null) {
      return { refusal: 'RECONCILIATION_RATE_UNAVAILABLE', donationId: donation.id };
    }
    if (credit === 'exceeds') {
      return { refusal: 'RECONCILIATION_EXCEEDS_OUTSTANDING', donationId: donation.id };
    }
    items.push({
      donationId: donation.id,
      transactionAmountMinor,
      creditedMinor: credit.creditedMinor,
      fxDifferenceMinor: credit.fxDifferenceMinor,
    });
    requiredMinor += transactionAmountMinor;
  }
  return { refusal: null, items, requiredMinor };
}

/**
 * The organization a donation is mainly paid through — docs/decisions/0128: the
 * one whose confirmed movements credited the most, in the donation's currency.
 * A tie keeps the current organization when it is one of the tied, and
 * otherwise takes the smallest id, so every reader chooses the same one.
 */
export function mainOrganizationOf(
  current: string | null,
  credited: readonly { readonly organizationId: string; readonly amountMinor: bigint }[],
): string | null {
  const byOrganization = new Map<string, bigint>();
  for (const row of credited) {
    byOrganization.set(
      row.organizationId,
      (byOrganization.get(row.organizationId) ?? 0n) + row.amountMinor,
    );
  }
  let best = -1n;
  let tied: string[] = [];
  for (const [organizationId, amountMinor] of byOrganization) {
    if (amountMinor > best) {
      best = amountMinor;
      tied = [organizationId];
    } else if (amountMinor === best) {
      tied.push(organizationId);
    }
  }
  if (tied.length === 0) return current;
  if (current !== null && tied.includes(current)) return current;
  return tied.sort()[0] ?? current;
}

/** The line-side amount still available on a line. */
export function lineRemainder(line: Pick<AllocationLine, 'amount' | 'allocatedMinor'>): Money {
  return money(line.amount.amountMinor - line.allocatedMinor, line.amount.currency);
}
